import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import { GET_EVENT_QUERY_KEY } from '../../event-management/hooks/useGetEvent';
import { GET_EVENT_WAITLIST_QUERY_KEY } from './useGetEventWaitlist';

/**
 * Reject / remove a waitlisted player (MYS-62). For a level-gated approval
 * request this is the "reject" action.
 */
export const useRemoveFromWaitlist = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ eventId, userId }: { eventId: number; userId: string }) =>
            apiClient.fetchApi<unknown, {}>({
                method: 'DELETE',
                endpoint: `events/${eventId}/waitlist/${userId}`,
            }),
        onSuccess: (_data, { eventId }) => {
            toast({
                title: translate('event.waitlist.reject_success'),
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            queryClient.invalidateQueries({ queryKey: GET_EVENT_WAITLIST_QUERY_KEY(eventId) });
            // The event too, exactly as `usePromoteFromWaitlist` does (H51).
            // `removeFromWaitlist` deletes the row and renumbers the entries
            // behind it, and `EventDto.waitlistCount` is derived server-side
            // from that same collection — so without this the cached event
            // keeps reporting the queue length from before the removal.
            queryClient.invalidateQueries({ queryKey: GET_EVENT_QUERY_KEY(eventId) });
        },
        onError: (error: any) => {
            toast({
                title: translate('event.waitlist.reject_error'),
                description: error?.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};
